import { Controller, Form } from 'react-hook-form';
import { isEqual, isNil } from 'lodash';
import {
  Box,
  Button,
  TextField,
  FormHelperText,
  Typography,
  Divider,
} from '@mui/material';
import { t } from 'i18next';
import { paths, USER_SUFFIX } from '@common/types';
import { useGeneral } from './hooks';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { USStateSelect } from 'components/USStateSelect';
import { SaveChangesModal } from '@components';
import { Select } from 'ui';

interface GeneralProps {
  user: paths[`/user/{user_id}`]['get']['responses']['200']['content']['application/json'] & {
    id: string;
  };
}

export const General = ({ user }: GeneralProps) => {
  const {
    roleFields,
    roleFieldIds,
    control,
    onSubmit,
    errors,
    isSubmitEnabled,
    isSubmitting,
    roleOptions,
    teamOptions,
    handleAddRole,
    handleRemoveRole,
    getRoleOptions,
    titles,
    getRoleRecordLabel,
    isAddRoleBtnDisabled,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
    setValue,
    duplicateRole,
    supervisorOptions,
  } = useGeneral(user);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <Form
        control={control}
        onSubmit={e => onSubmit(e.event)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          height: 'fit-content',
          borderRadius: 0,
          backgroundColor: 'white',
          position: 'relative',
          gap: '1rem',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            gap: '0.5rem',
          }}
        >
          <Controller
            name='first_name'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                size='medium'
                label={t('adminPanel.users.addModal.firstName')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                required
                fullWidth
              />
            )}
          />
          <Controller
            name='last_name'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                size='medium'
                label={t('adminPanel.users.addModal.lastName')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                required
                fullWidth
              />
            )}
          />
        </Box>

        <Controller
          name='suffix'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              value={field.value ?? ''}
              size='medium'
              label={t('adminPanel.users.addModal.optionalSuffix')}
              error={!isNil(error)}
              options={Object.values(USER_SUFFIX).map(o => ({
                value: o,
                label: o,
              }))}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name='title'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              value={field.value ?? ''}
              size='medium'
              label={t('adminPanel.users.addModal.titleLabel')}
              error={!isNil(error)}
              options={titles}
              helperText={error?.message}
              clearable
            />
          )}
        />

        <Controller
          name='team_id'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              size='medium'
              label={t('adminPanel.users.addModal.team')}
              error={!isNil(error)}
              options={teamOptions}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name='supervisor_id'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              options={supervisorOptions}
              name='supervisor_id'
              label='Supervisor (optional)'
              size='medium'
              error={!isNil(error)}
              helperText={error?.message}
              clearable
              clearLabel='No supervisor'
            />
          )}
        />

        <Box sx={{ width: '100%' }}>
          <Typography
            variant='subtitle2'
            sx={{
              width: '100%',
              textAlign: 'left',
            }}
          >
            {t('adminPanel.users.addModal.rolesTitle')}
          </Typography>

          {!isNil(errors['roles']) && (
            <FormHelperText
              error={!isNil(errors['roles'])}
              sx={{ color: 'red', width: '100%' }}
            >
              {errors['roles'] ? errors['roles'].message : null}
            </FormHelperText>
          )}
          <Box sx={{ width: '100%' }}>
            <Button
              startIcon={<AddIcon />}
              sx={{
                fontWeight: 'regular',
                fontSize: '1rem',
                color: 'primary.main',
              }}
              color='secondary'
              variant='text'
              disabled={isAddRoleBtnDisabled}
              onClick={handleAddRole}
            >
              {t('adminPanel.users.addModal.addRole')}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            gap: '1rem',
            flexDirection: 'column',
          }}
        >
          {roleFieldIds.map((fieldItem, index) => {
            const roleEntry = roleFields[index] ?? fieldItem;
            const role = roleOptions.find(
              role => role.role_name === roleEntry.role,
            );

            const isDuplicate = isEqual(roleEntry, duplicateRole);

            return (
              <Box
                key={fieldItem.id}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    gap: '1rem',
                    flexDirection: 'column',
                  }}
                >
                  <Controller
                    name={`roles.${index}.role`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Select
                        {...field}
                        size='medium'
                        label={`${t('adminPanel.users.addModal.role')} ${index + 1}`}
                        error={!isNil(error)}
                        required
                        options={roleOptions.map(o => ({
                          value: o.role_name,
                          label: o.display_name,
                        }))}
                        onChange={event => {
                          field.onChange(event);
                          setValue(`roles.${index}.record_id`, '', {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                        helperText={error?.message}
                      />
                    )}
                  />

                  <Controller
                    name={`roles.${index}.record_id`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Select
                        {...field}
                        size='medium'
                        label={getRoleRecordLabel(role)}
                        error={!isNil(error) || isDuplicate}
                        required
                        options={getRoleOptions(role).map(o => ({
                          value: o.id,
                          label: o.name,
                        }))}
                        helperText={
                          isDuplicate
                            ? `This role has already been assigned to this ${getRoleRecordLabel(role)}.`
                            : error?.message
                        }
                      />
                    )}
                  />
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Button
                    startIcon={<DeleteIcon />}
                    sx={{
                      fontWeight: 'regular',
                      fontSize: '0.75rem',
                      color: 'error',
                      mt: '0.25rem',
                    }}
                    size='small'
                    color='error'
                    variant='text'
                    onClick={() => handleRemoveRole(index)}
                  >
                    {t('adminPanel.users.addModal.removeRole')}
                  </Button>
                </Box>

                {roleFieldIds.length > 1 && (
                  <Divider
                    orientation='horizontal'
                    sx={{
                      width: '100%',
                      marginTop: '0.5rem',
                      marginBottom: '0.25rem',
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        <Typography
          variant='subtitle2'
          sx={{
            width: '100%',
            textAlign: 'left',
          }}
        >
          {t('adminPanel.users.addModal.contactInformation')}
        </Typography>

        <Controller
          name='phone_number'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              size='medium'
              label={t('adminPanel.users.addModal.phoneNumber')}
              variant='outlined'
              error={!isNil(error)}
              helperText={error ? error.message : null}
              fullWidth
            />
          )}
        />

        <Controller
          name='mobile_phone_number'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              size='medium'
              label={t('adminPanel.users.addModal.mobilePhoneNumber')}
              variant='outlined'
              error={!isNil(error)}
              helperText={error ? error.message : null}
              fullWidth
            />
          )}
        />

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            gap: '0.5rem',
          }}
        >
          <Controller
            name='street'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value || ''}
                type='text'
                size='medium'
                label={t('adminPanel.users.addModal.addressLine1')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                fullWidth
              />
            )}
          />
          <Controller
            name='suite'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value || ''}
                type='text'
                size='medium'
                label={t('adminPanel.users.addModal.addressLine2')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                fullWidth
              />
            )}
          />
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            gap: '0.5rem',
          }}
        >
          <Controller
            name='city'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value || ''}
                type='text'
                size='medium'
                label={t('adminPanel.users.addModal.city')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                fullWidth
              />
            )}
          />
          <Controller
            name='state'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <USStateSelect
                {...field}
                value={field.value ?? undefined}
                size='medium'
                error={!!error}
                helperText={error ? error.message : ''}
              />
            )}
          />
        </Box>

        <Controller
          name='zip_code'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ''}
              type='text'
              size='medium'
              label={t('adminPanel.users.addModal.zip')}
              variant='outlined'
              error={!isNil(error)}
              helperText={error ? error.message : null}
              fullWidth
            />
          )}
        />

        <Controller
          name='county'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value || ''}
              type='text'
              size='medium'
              label={t('adminPanel.users.addModal.county')}
              variant='outlined'
              error={!isNil(error)}
              helperText={error ? error.message : null}
              fullWidth
            />
          )}
        />

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            pt: '0.5rem',
            position: 'sticky',
            bottom: '0',
            left: 0,
            bgcolor: theme => theme.palette.common.white,
            zIndex: 10,
          }}
        >
          <Button
            type='submit'
            variant='contained'
            color='secondary'
            sx={{
              fontSize: '0,875rem',
              fontWeight: 'medium',
              lineHeight: '24px',
              color: 'white',
            }}
            disabled={!isSubmitEnabled || !!duplicateRole}
            loading={isSubmitting}
            loadingPosition='center'
          >
            {t('general.saveChanges')}
          </Button>
        </Box>
      </Form>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid && !duplicateRole}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </Box>
  );
};
